import "flexlayout-react/style/dark.css";
import "react-toastify/dist/ReactToastify.css";

import { DirectorySidebar } from "@app-components/layout/DirectorySideBar";
import { EnvironmentConfigModal } from "@app-components/modal/EnvironmentConfigModal/EnvironmentConfigModal";
import { AuthProvider } from "@app-context/authorization/AuthProvider";
import { useEnvironments } from "@app-context/environments/context";
import { EnvironmentsProvider } from "@app-context/environments/EnvironmentsProvider";
import { InspectorContextProvider } from "@app-context/inspector/InspectorContextProvider";
import { NetworkCallsProvider } from "@app-context/networkCalls/NetworkCallsProvider";
import { FACTSET_LOADED_KEY } from "@enfusion-ui/core";
import { useRefCallback, useUserAgent } from "@enfusion-ui/hooks";
import {
  BrowserErrorView,
  Button,
  IconButton,
  SideBar,
  SidebarProvider,
  TOP_APP_BAR_HEIGHT,
  TopAppBar,
  UndockedView,
} from "@enfusion-ui/web-components";
import {
  checkBrowserCompatibility,
  GlobalStyles,
  styled,
  TabDef,
  ThemeProvider,
  useAuth,
  useIsMobileDevice,
  useIsTabletDevice,
  useTabs,
} from "@enfusion-ui/web-core";
import { TabLayout, TabsProvider } from "@enfusion-ui/web-layout";
import { WorkerProvider } from "@enfusion-ui/web-workers";
import {
  faCog,
  faHandPointDown,
  faHandPointLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useBeforeUnload } from "react-use";
import { InspectorView } from "view/Inspector";

import EntryWorker from "./workers/entry.worker.ts";

const LayoutWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: absolute;
  flex-direction: column;
`;

const MainWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const SidebarContainer = styled.div`
  overflow: hidden;
  height: 100%;
`;
const StyledButton = styled(Button)<{ isLoggedIn?: boolean }>`
  margin-left: 5px;
  background-color: transparent;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  margin-inline-start: auto;
`;

export function renderTab(def: TabDef) {
  switch (def.component) {
    case "file":
    case "inspector": {
      return (
        <InspectorContextProvider config={def.config}>
          <InspectorView />
        </InspectorContextProvider>
      );
    }
    case "user-settings": {
      return <>user-settings</>;
    }
    default: {
      return <>Need renderer for {def.component}</>;
    }
  }
}

const AuthenticatedAppCore: React.FC<{
  linked?: boolean;
}> = ({ linked }) => {
  const { authEnvironment } = useEnvironments();
  const { layoutModel, undockTab } = useTabs();
  const isMobileDevice = useIsMobileDevice();
  const isTabletDevice = useIsTabletDevice();
  const [resizeCountIgnored, setResizeCount] = React.useState(0);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const history = useHistory();
  const { user, login, logout } = useAuth();
  const handleHotkey = useRefCallback(() => {
    const selectedNode = layoutModel?.getActiveTabset()?.getSelectedNode();
    if (!!selectedNode) {
      undockTab(selectedNode.getId());
    }
  }, [layoutModel]);
  useHotkeys("ctrl+d", handleHotkey);

  useBeforeUnload(() => {
    localStorage.removeItem(FACTSET_LOADED_KEY);
    return false;
  }, "");

  React.useEffect(() => {
    const parseLinked = async () => {
      // eslint-disable-next-line no-restricted-globals
      // const parsed = queryString.parse(location.search);
    };

    if (linked) {
      parseLinked();
      history.replace("/");
    }
  }, [linked]);

  React.useEffect(() => {
    sessionStorage.clear();
  }, []);

  const handleSidebarResize = () =>
    setResizeCount((state) => (state === 100 ? 0 : state + 1));

  const handleClick = useRefCallback(() => {
    if (user) {
      logout();
    } else {
      login({}, "");
    }
  }, [user]);

  return (
    <LayoutWrapper>
      <TopAppBar
        title="API Inspector 1.0.0"
        titleRightContent={
          <Action>
            {user && `${user?.username} (${authEnvironment.label})`}
            <StyledButton onClick={handleClick}>
              {user ? "Logout" : `Login (${authEnvironment.label})`}
            </StyledButton>
            <IconButton
              title="Settings"
              icon={faCog}
              size="sm"
              hoverable
              onClick={() => setSettingsOpen(true)}
            />
          </Action>
        }
      />

      <EnvironmentConfigModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <MainWrapper>
        <SidebarContainer>
          <SidebarProvider
            noUser
            storageKey="ai-tool-sidebar-size"
            onResize={handleSidebarResize}
          >
            <SideBar
              open
              fullWidth={isMobileDevice}
              heightOffset={isTabletDevice ? 0 : TOP_APP_BAR_HEIGHT}
            >
              <DirectorySidebar />
            </SideBar>
          </SidebarProvider>
        </SidebarContainer>
        <TabLayout
          renderTab={renderTab}
          emptyMessage={
            <>
              <FontAwesomeIcon
                icon={isMobileDevice ? faHandPointDown : faHandPointLeft}
                size="3x"
                style={{ marginRight: "var(--spacing-xxl)" }}
              />
              Welcome to the API Inspector. Please select a file or open a new
              tab from the {isMobileDevice ? "bottom" : "left"} to begin.
            </>
          }
        />
      </MainWrapper>
    </LayoutWrapper>
  );
};

const AuthenticatedApp: React.FC<{ linked?: boolean; undocked?: boolean }> = ({
  linked,
  undocked = false,
}) => {
  const isMobileDevice = useIsMobileDevice();

  return (
    <>
      {undocked ? (
        <UndockedView renderTab={renderTab} />
      ) : (
        <AuthenticatedAppCore linked={linked} />
      )}
      <ToastContainer
        autoClose={5000}
        position={isMobileDevice ? "bottom-right" : "top-right"}
        hideProgressBar
        pauseOnHover
        closeOnClick={false}
        draggable
      />
    </>
  );
};

function AppEntry() {
  const agent = useUserAgent();

  const [isCompatible, setIsCompatible] = React.useState(true);

  React.useEffect(() => {
    setIsCompatible(checkBrowserCompatibility(agent));
  }, []);

  return isCompatible ? (
    <TabsProvider forceNoUser storageKeyBase="ai-tool-tab-state">
      <Router>
        <Switch>
          <Route path="/linking">
            <AuthenticatedApp linked />
          </Route>
          <Route path="/pop-out">
            <AuthenticatedApp undocked />
          </Route>
          <Route path="/">
            <AuthenticatedApp />
          </Route>
        </Switch>
      </Router>
    </TabsProvider>
  ) : (
    <BrowserErrorView />
  );
}

const App = () => {
  return (
    <EnvironmentsProvider>
      <AuthProvider user={null}>
        <WorkerProvider EntryWorker={EntryWorker}>
          <NetworkCallsProvider>
            <ThemeProvider>
              <GlobalStyles />
              <AppEntry />
            </ThemeProvider>
          </NetworkCallsProvider>
        </WorkerProvider>
      </AuthProvider>
    </EnvironmentsProvider>
  );
};

export default App;
